import { useApplicationStore } from '@data';
import { tracking } from '@lib/events';
import { serviceIsAddictionTreatment } from '@pages/SchedulePage/utils';
import { INTAKE_QUIZ_ROUTE_PATHNAME } from '@router/routes/routePathnames';

/**
 * Idempotency key for the session-scoped dedupe flag. Once a user has been
 * enrolled in the MAT experiment cohort within a given browser session, we
 * skip re-firing `trackExperimentStarted` from subsequent loader runs (e.g.
 * navigating from /mat-questions → /schedule, or refreshing the page) so
 * Mixpanel sees one enrollment per cohort entry rather than several.
 *
 * Also doubles as an explicit exclusion marker: when set to
 * `EXCLUDED_MARKER`, no loader will ever fire `trackExperimentStarted` for
 * the rest of the session. We use this when intake-quiz session creation
 * fails — those users never saw the experiment, so they shouldn't pollute
 * the cohort.
 */
export const ENROLLMENT_KEY = 'experiment:mat_questions:enrolled';
const EXCLUDED_MARKER = 'excluded';

/**
 * Enroll the user in the MAT-questions A/B experiment cohort when they reach
 * the MOUD scheduling funnel. Idempotent within a session.
 *
 * Call from every loader that can be a first entry point into the funnel —
 * `/mat-questions`, `/schedule`, and the curated-slots variant — so users
 * who deep-link directly to the schedule page (bypassing the services-page
 * click) aren't missed.
 *
 * Gating:
 * - Not logged in (logged-in patients can't be in a new-patient experiment).
 * - `experimentMatQuestionsEnabled` kill switch is on.
 * - Visit type's parent service is addiction treatment.
 *
 * Variant assignment is still deterministic from Firebase's installation id
 * via the `experimentMatQuestions` flag; this helper only controls *when*
 * Mixpanel is told the user enrolled, not *which* arm they're in. Because
 * bucket and visit-type selection are independent random variables, deferring
 * enrollment to the qualifying moment preserves the 50/50 split within the
 * cohort.
 */
export function enrollInMatExperimentIfQualifies(
  visitTypeId: string | undefined,
): void {
  if (typeof window === 'undefined') return;
  if (!visitTypeId) return;
  if (sessionStorage.getItem(ENROLLMENT_KEY)) return;

  const { auth, patient, flags, serviceTypes } =
    useApplicationStore.getState();

  const isLoggedIn = Boolean(auth?.accessToken && patient?.patientProfile);
  if (isLoggedIn) return;
  if (!flags.experimentMatQuestionsEnabled) return;

  const parentServiceType = serviceTypes.find((st) =>
    st.visitTypes.some((v) => v.visitTypeId === visitTypeId),
  );
  if (
    !parentServiceType ||
    !serviceIsAddictionTreatment(parentServiceType.serviceTypeId)
  ) {
    return;
  }

  tracking.trackExperimentStarted({
    experimentName: 'mat_questions',
    variantName: flags.experimentMatQuestions ? 'treatment' : 'control',
  });
  sessionStorage.setItem(ENROLLMENT_KEY, '1');
}

/**
 * Mark the user as explicitly excluded from the MAT experiment cohort for
 * the rest of the browser session. Subsequent calls to
 * `enrollInMatExperimentIfQualifies` (from any loader — `/schedule`,
 * curated-slots, etc.) will short-circuit and never fire
 * `trackExperimentStarted`.
 *
 * Use when the intake-quiz session creation fails on the page entry —
 * the user is bumped to the standard scheduling flow without ever seeing
 * the first question, and we don't want to count them as cohort members.
 */
export function markExcludedFromMatExperiment(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(ENROLLMENT_KEY, EXCLUDED_MARKER);
}

/**
 * Returns the `/intake-quiz/<visitTypeId>` redirect path when the user
 * qualifies for the MAT-questions treatment arm and hasn't already been
 * through (or excluded from) the quiz this session; otherwise `null`.
 *
 * Use in every loader that can route a treatment-arm user past the intake
 * quiz — `rootLoader` (UTM `?utm_service=` deep links), `scheduleLoader`
 * (direct deep links to `/schedule/<id>`), and the curated-slots loader —
 * so that the only treatment-arm patients who skip the quiz are the ones
 * we've already excluded (BE bootstrap failure) or who've already completed
 * it. Without this, treatment-arm patients entering the funnel via UTM or
 * deep link fire `Experiment Started` from the destination loader but
 * never see a question, inflating the treatment-cohort denominator.
 *
 * Conditions mirror `enrollInMatExperimentIfQualifies` exactly, plus:
 * - skip when `intakeQuiz.completed` is true (avoid bouncing back into
 *   the quiz after completion)
 * - skip when the session was marked excluded (avoid looping into a
 *   failing BE bootstrap)
 * - skip when the user is in control arm (they belong on /schedule)
 */
export function getIntakeQuizRedirectPath(
  visitTypeId: string | undefined,
): string | null {
  if (typeof window === 'undefined') return null;
  if (!visitTypeId) return null;

  if (sessionStorage.getItem(ENROLLMENT_KEY) === EXCLUDED_MARKER) return null;

  const { auth, patient, flags, serviceTypes, intakeQuiz } =
    useApplicationStore.getState();

  if (intakeQuiz.completed) return null;

  const isLoggedIn = Boolean(auth?.accessToken && patient?.patientProfile);
  if (isLoggedIn) return null;
  if (!flags.experimentMatQuestionsEnabled) return null;
  if (!flags.experimentMatQuestions) return null;

  const parentServiceType = serviceTypes.find((st) =>
    st.visitTypes.some((v) => v.visitTypeId === visitTypeId),
  );
  if (
    !parentServiceType ||
    !serviceIsAddictionTreatment(parentServiceType.serviceTypeId)
  ) {
    return null;
  }

  return `${INTAKE_QUIZ_ROUTE_PATHNAME}/${visitTypeId}`;
}
